import { computed, Ref, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useUserPortalStore } from "../pinia/userPortal";
import { getAclStatus } from "../shared/services/aclStatus";
import { isNativePlatform } from "../capacitor/platform";
import { isValidHttpUrl, selectReferrerMatch } from "./urlHelpers";

export const REFERRER_QUERY_KEY: string = "referrer";
export const REDIRECT_TARGET_QUERY_KEY: string = "target";

interface ACL {
  ACLID?: string;
  URL: string;
  Status: number;
  AutoBrowseUsers?: boolean;
  InstantReferrerRedirect?: boolean;
}

const REDIRECT_COUNTDOWN_SECONDS = 4;

interface UseRedirectUrlReturn {
  redirectUrl: Ref<string | null>;
  redirectSeconds: Ref<number>;
  shouldShowRedirect: Ref<boolean>;
  cancelRedirect: () => Promise<void>;
  getReferrerUrl: () => string | null;
}

/**
 * Redirect URL logic for referrer and auto-browse flows. On web, ACLs
 * with InstantReferrerRedirect are handled server-side via 303; without
 * it the SPA shows a countdown. Native retains full client-side logic.
 */
export function useRedirectUrl(acls: Ref<Array<ACL>> | Array<ACL>): UseRedirectUrlReturn {
  const router = useRouter();
  const route = useRoute();
  const userPortal = useUserPortalStore();

  const referrerFromQuery = (route.query[REFERRER_QUERY_KEY] as string) || null;
  const referrerUrl = ref<string | null>(referrerFromQuery);

  const aclsValue = computed(() => (typeof acls === "object" && "value" in acls ? acls.value : acls));

  const isGrantLive = (statusCode: number): boolean => {
    const status = getAclStatus(statusCode);
    return status?.grantIssued === true && status.unstable === false;
  };

  const autoBrowseAcl = computed<ACL | null>(() => {
    if (aclsValue.value.length != 1) {
      return null;
    }
    const autoBrowseAcls = aclsValue.value.filter((acl) => acl.AutoBrowseUsers === true);
    if (autoBrowseAcls.length !== 1) {
      return null;
    }
    const acl = autoBrowseAcls[0];
    if (!isGrantLive(acl.Status)) {
      return null;
    }
    if (!isValidHttpUrl(acl.URL)) {
      return null;
    }
    return acl;
  });

  const autoBrowseUrl = computed(() => autoBrowseAcl.value?.URL ?? null);

  const referrerMatchAcl = computed<ACL | null>(() => {
    if (!isValidHttpUrl(referrerUrl.value) || !referrerUrl.value) {
      return null;
    }
    return selectReferrerMatch(referrerUrl.value, aclsValue.value, {
      eligible: (acl) => isGrantLive(acl.Status),
    });
  });

  const dropIfCancelled = (candidate: string | null): string | null =>
    candidate && !userPortal.isRedirectCancelled(candidate) ? candidate : null;

  const redirectUrl = computed(() => {
    if (isNativePlatform()) {
      if (autoBrowseUrl.value) {
        if (referrerMatchAcl.value && referrerUrl.value) {
          return dropIfCancelled(referrerUrl.value);
        }
        return dropIfCancelled(autoBrowseUrl.value);
      }
      if (referrerMatchAcl.value && referrerUrl.value) {
        return dropIfCancelled(referrerUrl.value);
      }
      return null;
    }

    if (referrerMatchAcl.value && referrerUrl.value && !referrerMatchAcl.value.InstantReferrerRedirect) {
      return dropIfCancelled(referrerUrl.value);
    }
    if (autoBrowseAcl.value && !autoBrowseAcl.value.InstantReferrerRedirect) {
      return dropIfCancelled(autoBrowseUrl.value);
    }
    return null;
  });

  const redirectSeconds = computed(() => REDIRECT_COUNTDOWN_SECONDS);

  const shouldShowRedirect = computed(() => {
    return redirectUrl.value !== null && redirectSeconds.value > 0;
  });

  const cancelRedirect = async (): Promise<void> => {
    userPortal.markRedirectCancelled(redirectUrl.value);

    referrerUrl.value = null;

    if (route.query.referrer) {
      const newQuery = { ...route.query };
      delete newQuery[REFERRER_QUERY_KEY];
      await router.replace({ query: newQuery });
    }
  };

  const getReferrerUrl = (): string | null => {
    if (isValidHttpUrl(referrerUrl.value)) {
      return referrerUrl.value;
    }
    return null;
  };

  return {
    redirectUrl,
    redirectSeconds,
    shouldShowRedirect,
    cancelRedirect,
    getReferrerUrl,
  };
}
